import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { getThemeColors } from '../utils/theme';
import SheetSelector from './SheetSelector';
import PaginatedTable from './PaginatedTable';
import HelpIcon from './HelpIcon';
import { API_ENDPOINTS } from '../config.js';
import { authenticatedFetchJson } from '../utils/api.js';
import { getHelpContent } from '../utils/helpContent.js';

function isCellInRange(row, col, range) {
  if (!range) return false;
  const minRow = Math.min(range.start.row, range.end.row);
  const maxRow = Math.max(range.start.row, range.end.row);
  const minCol = Math.min(range.start.col, range.end.col);
  const maxCol = Math.max(range.start.col, range.end.col);
  return row >= minRow && row <= maxRow && col >= minCol && col <= maxCol;
}

export default function FilePreview({ preview, setPreview, onSheetChange, onCellChange, onTranslateSelected, onUndo, onRedo, onCopy, onPaste, onExportSelected, scriptMode = 'latin', onToggleScript, onAutoSaveReady, canUndo = false }) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const colors = getThemeColors(isDark);
  // ALL HOOKS MUST BE DECLARED BEFORE ANY EARLY RETURNS
  const [editingCell, setEditingCell] = useState(null); // { row, col }
  const [editValue, setEditValue] = useState('');
  const [selectedRange, setSelectedRange] = useState(null); // { start: {row, col}, end: {row, col} }
  const [isSelecting, setIsSelecting] = useState(false);
  const tableRef = useRef();
  const [contextMenu, setContextMenu] = useState(null); // { x, y }
  const ignoreNextFocus = useRef(false);
  const [undoStack, setUndoStack] = useState([]);
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [currencyForm, setCurrencyForm] = useState({
    currency: 'EUR',
    date: new Date().toISOString().slice(0, 10),
    manualRate: '',
    simpleMultiply: false
  });
  const [preloadedRate, setPreloadedRate] = useState(null);
  const [loadingRate, setLoadingRate] = useState(false);

  // Draft management state
  const [draftData, setDraftData] = useState(null); // { exists, sheetData, lastSavedBy, lastSavedAt, loaded }
  const [draftLoading, setDraftLoading] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [lastLoadedAt, setLastLoadedAt] = useState(null); // Track when file was last loaded
  const [saving, setSaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false); // Track auto-save state separately
  const [hasConflict, setHasConflict] = useState(false);
  const [conflictedCells, setConflictedCells] = useState(new Set()); // Set of "row-col" strings
  const [conflictMetadata, setConflictMetadata] = useState(new Map()); // Map of "row-col" -> { editedBy, editedAt }
  
  // Debounce timer ref for auto-save
  const autoSaveTimerRef = useRef(null);

  // Save draft for a specific sheet (used for individual sheet saves)
  const saveDraftForSheet = useCallback(async (sheetIndex, sheetData, skipSavingCheck = false, isAutoSave = false) => {
    if (!preview?.fileId) return { success: false };
    // Only check saving flag if not skipping (skip when called from saveDraft which manages the flag)
    if (!skipSavingCheck && saving && !isAutoSave) return { success: false };
    
    try {
      const data = await authenticatedFetchJson(
        `${API_ENDPOINTS.SAVE_FILE_DRAFT}/${preview.fileId}/draft`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sheetData: sheetData,
            sheetIndex: sheetIndex,
            lastLoadedAt: lastLoadedAt // Send when user last loaded the file
          })
        }
      );
      
      if (data.success) {
        if (data.hasConflict && data.conflictedCells && Array.isArray(data.conflictedCells)) {
          console.warn(`Conflict detected while saving draft for sheet ${sheetIndex}:`, data.conflictedCells.length, 'conflicted cells');
          
          // Update conflicted cells state
          const newConflictedCells = new Set();
          const newConflictMetadata = new Map();
          
          data.conflictedCells.forEach(conflict => {
            const cellKey = `${conflict.row}-${conflict.col}`;
            newConflictedCells.add(cellKey);
            newConflictMetadata.set(cellKey, {
              editedBy: conflict.editedBy,
              editedAt: conflict.editedAt,
              value: conflict.value
            });
          });
          
          setConflictedCells(newConflictedCells);
          setConflictMetadata(newConflictMetadata);
          setHasConflict(true);
        } else {
          // Clear conflicts if no conflicts detected
          setConflictedCells(new Set());
          setConflictMetadata(new Map());
          setHasConflict(false);
        }
      }
      return data;
    } catch (err) {
      console.error(`[saveDraftForSheet] Failed to save draft for sheet ${sheetIndex}:`, err);
      return { success: false, error: err };
    }
  }, [preview, saving, lastLoadedAt]);

  // Debounced auto-save function - saves current sheet after delay
  const triggerAutoSave = useCallback(() => {
    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // Set auto-saving indicator
    setAutoSaving(true);
    
    // Set new timer (2.5 second delay)
    autoSaveTimerRef.current = setTimeout(async () => {
      // Get latest state using functional update
      setPreview(prev => {
        if (!prev?.fileId || !prev?.rows || !prev?.sheetNames) {
          setAutoSaving(false);
          autoSaveTimerRef.current = null;
          return prev;
        }
        
        const currentSheetIndex = prev.currentSheet;
        const currentSheetName = prev.sheetNames[currentSheetIndex];
        const currentRows = prev.rows;
        
        // Update sheets object with current rows before saving
        const updatedSheets = { ...prev.sheets };
        if (currentSheetName) {
          updatedSheets[currentSheetName] = currentRows;
        }
        
        // Save asynchronously
        (async () => {
          try {
            const result = await saveDraftForSheet(currentSheetIndex, currentRows, false, true);
            if (result?.success) {
              setLastSavedAt(new Date().toISOString());
              // Conflict handling is done in saveDraftForSheet
              console.log('[triggerAutoSave] Auto-saved sheet', currentSheetIndex);
            }
          } catch (err) {
            console.error('[triggerAutoSave] Failed to auto-save:', err);
          } finally {
            setAutoSaving(false);
            autoSaveTimerRef.current = null;
          }
        })();
        
        return { ...prev, sheets: updatedSheets };
      });
    }, 2500); // 2.5 second debounce
  }, [saveDraftForSheet, setPreview]);

  // Cleanup timer on unmount or when file changes
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, [preview?.fileId]);

  // Expose auto-save function to parent component
  useEffect(() => {
    if (onAutoSaveReady) {
      onAutoSaveReady(triggerAutoSave);
    }
  }, [onAutoSaveReady, triggerAutoSave]);

  // Manual save draft function - saves ALL sheets
  const saveDraft = useCallback(async () => {
    if (!preview?.fileId || saving) return;
    
    setSaving(true);
    try {
      // Update sheets object with current rows before saving (in case user made edits and didn't switch sheets)
      const allSheets = preview.sheets || {};
      if (preview.sheetNames && preview.sheetNames[preview.currentSheet]) {
        const currentSheetName = preview.sheetNames[preview.currentSheet];
        allSheets[currentSheetName] = preview.rows;
      }
      
      console.log('[saveDraft] Saving drafts for all sheets:', { 
        fileId: preview.fileId, 
        sheetCount: preview.sheetNames?.length || 0,
      });
      
      // Save all sheets (skip saving check since we manage it here)
      const savePromises = (preview.sheetNames || []).map((sheetName, sheetIndex) => {
        const sheetData = allSheets[sheetName] || [];
        return saveDraftForSheet(sheetIndex, sheetData, true);
      });
      
      const results = await Promise.all(savePromises);
      
      // Check if all saves were successful
      const allSuccessful = results.every(r => r?.success !== false);
      const hasAnyConflict = results.some(r => r?.hasConflict);
      
      if (allSuccessful) {
        setLastSavedAt(new Date().toISOString());
        // Conflict handling is done in saveDraftForSheet for each sheet
        console.log('[saveDraft] All drafts saved successfully');
      } else {
        const failedCount = results.filter(r => r?.success === false).length;
        alert(`Greška pri čuvanju draft-a. ${failedCount} od ${results.length} lista nije sačuvan.`);
      }
    } catch (err) {
      console.error('[saveDraft] Failed to save drafts:', err);
      alert('Greška pri čuvanju draft-a. Pokušajte ponovo.');
    } finally {
      setSaving(false);
    }
  }, [preview, saving, saveDraftForSheet]);

  // Handle sheet change with auto-save before switching
  const handleSheetChangeWithAutoSave = useCallback(async (newSheetIndex) => {
    if (newSheetIndex === preview?.currentSheet) return; // No change needed
    
    // Auto-save current sheet before switching
    if (preview?.fileId && preview?.rows && preview?.sheetNames) {
      const currentSheetIndex = preview.currentSheet;
      const currentSheetName = preview.sheetNames[currentSheetIndex];
      
      // Update sheets object with current rows
      if (currentSheetName && preview.sheets) {
        const updatedSheets = { ...preview.sheets };
        updatedSheets[currentSheetName] = preview.rows;
        setPreview(prev => ({ ...prev, sheets: updatedSheets }));
        
        // Auto-save current sheet
        try {
          await saveDraftForSheet(currentSheetIndex, preview.rows);
          console.log(`[handleSheetChangeWithAutoSave] Auto-saved sheet ${currentSheetIndex} before switching`);
        } catch (err) {
          console.error(`[handleSheetChangeWithAutoSave] Failed to auto-save sheet ${currentSheetIndex}:`, err);
        }
      }
    }
    
    // Switch to new sheet
    if (onSheetChange) {
      onSheetChange(newSheetIndex);
    }
  }, [preview, onSheetChange, saveDraftForSheet, setPreview]);

  // Load draft when file opens or sheet changes
  useEffect(() => {
    if (!preview?.fileId) return;
    
    const loadDraft = async () => {
      setDraftLoading(true);
      try {
        const data = await authenticatedFetchJson(
          `${API_ENDPOINTS.GET_FILE_DRAFT}/${preview.fileId}/draft?sheet=${preview.currentSheet || 0}`
        );
        
        console.log('[loadDraft] Draft response:', { exists: data.exists, hasSheetData: !!data.sheetData, fileId: preview.fileId, sheet: preview.currentSheet });
        
        // Update lastLoadedAt when file is loaded
        setLastLoadedAt(new Date().toISOString());
        
        // Clear conflicts when loading a new sheet
        setConflictedCells(new Set());
        setConflictMetadata(new Map());
        setHasConflict(false);
        
        if (data.exists) {
          setDraftData({ ...data, loaded: false }); // Show prompt
        } else {
          setDraftData({ exists: false, loaded: true });
        }
      } catch (err) {
        console.error('Failed to load draft:', err);
        setDraftData({ exists: false, loaded: true });
        // Still update lastLoadedAt even on error
        setLastLoadedAt(new Date().toISOString());
      }
      setDraftLoading(false);
    };
    
    loadDraft();
  }, [preview?.fileId, preview?.currentSheet]);

  // Preload exchange rate when modal opens or currency/date changes
  useEffect(() => {
    if (!showCurrencyModal || currencyForm.simpleMultiply) {
      setPreloadedRate(null);
      return;
    }

    // Only preload if manual rate is not set
    if (currencyForm.manualRate && currencyForm.manualRate.trim().length > 0) {
      setPreloadedRate(null);
      return;
    }

    // Validate currency and date before fetching
    if (!currencyForm.currency || currencyForm.currency.trim().length === 0) {
      setPreloadedRate(null);
      return;
    }

    if (!currencyForm.date) {
      setPreloadedRate(null);
      return;
    }

    // Format date
    let formattedDate = currencyForm.date;
    if (formattedDate.includes('/')) {
      const dateParts = formattedDate.split('/');
      if (dateParts.length === 3) {
        formattedDate = `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;
      }
    }

    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(formattedDate)) {
      setPreloadedRate(null);
      return;
    }

    // Fetch exchange rate
    const fetchRate = async () => {
      setLoadingRate(true);
      try {
        const res = await fetch(`${API_ENDPOINTS.EXCHANGE_RATE}/${currencyForm.currency.toUpperCase()}/${formattedDate}`);
        const data = await res.json();
        if (res.ok && data.exchange_middle) {
          const rate = parseFloat(data.exchange_middle);
          if (!isNaN(rate) && rate > 0) {
            setPreloadedRate(rate);
          } else {
            setPreloadedRate(null);
          }
        } else {
          setPreloadedRate(null);
        }
      } catch (err) {
        console.error('Failed to preload exchange rate:', err);
        setPreloadedRate(null);
      } finally {
        setLoadingRate(false);
      }
    };

    fetchRate();
  }, [showCurrencyModal, currencyForm.currency, currencyForm.date, currencyForm.simpleMultiply, currencyForm.manualRate]);

  // NOW safe to do early returns after all hooks
  if (!preview) return <h1 style={{fontSize: 40, fontWeight: 900, color: '#2563eb'}}>Dobrodošli na Konverter</h1>;
  if (preview.loading) return <div>Učitavanje fajla...</div>;
  if (preview.error) return <div style={{ color: '#ef4444', fontWeight: 700 }}>{preview.error}</div>;
  if (!preview.rows) return null;
  const maxCols = preview.rows.reduce((max, row) => Math.max(max, row.length), 0);

  // Mouse selection handlers
  const handleCellMouseDown = (rowIdx, colIdx) => {
    setSelectedRange({ start: { row: rowIdx, col: colIdx }, end: { row: rowIdx, col: colIdx } });
    setIsSelecting(true);
  };
  const handleCellMouseOver = (rowIdx, colIdx) => {
    if (isSelecting && selectedRange) {
      setSelectedRange(range => ({ ...range, end: { row: rowIdx, col: colIdx } }));
    }
  };
  const handleMouseUp = () => {
    setIsSelecting(false);
  };
  // Keyboard navigation
  const handleTableKeyDown = (e) => {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const ctrlKey = isMac ? e.metaKey : e.ctrlKey;
    
    // Handle Ctrl+A / Cmd+A: Select all cells
    if (ctrlKey && e.key === 'a') {
      e.preventDefault();
      if (preview && preview.rows && preview.rows.length > 0) {
        const rowCount = preview.rows.length;
        const colCount = maxCols;
        setSelectedRange({
          start: { row: 0, col: 0 },
          end: { row: rowCount - 1, col: colCount - 1 }
        });
      }
      return;
    }
    
    // Handle Ctrl+C / Cmd+C: Copy
    if (ctrlKey && e.key === 'c' && !e.shiftKey) {
      e.preventDefault();
      if (selectedRange && onCopy) {
        onCopy(selectedRange);
      }
      return;
    }
    
    // Handle Ctrl+V / Cmd+V: Paste
    if (ctrlKey && e.key === 'v' && !e.shiftKey) {
      e.preventDefault();
      if (selectedRange && onPaste) {
        onPaste(selectedRange);
      }
      return;
    }
    
    // Handle Ctrl+Z / Cmd+Z: Undo
    if (ctrlKey && e.key === 'z' && !e.shiftKey) {
      e.preventDefault();
      if (onUndo) {
        onUndo();
      }
      return;
    }
    
    // Handle Ctrl+Y / Cmd+Y or Ctrl+Shift+Z / Cmd+Shift+Z: Redo
    if ((ctrlKey && e.key === 'y') || (ctrlKey && e.key === 'z' && e.shiftKey)) {
      e.preventDefault();
      if (onRedo) {
        onRedo();
      }
      return;
    }
    
    // Continue with existing navigation logic
    if (!selectedRange) return;
    const { start, end } = selectedRange;
    let anchor = editingCell || end || start;
    let { row, col } = anchor;
    let nextRow = row, nextCol = col;
    const rowCount = preview.rows.length;
    const colCount = maxCols;
    const move = (dr, dc) => {
      nextRow = Math.max(0, Math.min(rowCount - 1, row + dr));
      nextCol = Math.max(0, Math.min(colCount - 1, col + dc));
    };
    if (e.key === 'ArrowDown') move(1, 0);
    else if (e.key === 'ArrowUp') move(-1, 0);
    else if (e.key === 'ArrowLeft') move(0, -1);
    else if (e.key === 'ArrowRight') move(0, 1);
    else if (e.key === 'Tab') { e.preventDefault(); move(0, 1); }
    else if (e.key === 'Enter') { e.preventDefault(); move(1, 0); }
    else if (e.key === 'Escape') { setEditingCell(null); return; }
    else return;
    if (e.shiftKey) {
      setSelectedRange(range => ({ ...range, end: { row: nextRow, col: nextCol } }));
    } else {
      setSelectedRange({ start: { row: nextRow, col: nextCol }, end: { row: nextRow, col: nextCol } });
      setEditingCell(null);
    }
    // Ensure row is visible (for paginated tables)
    if (tableRef.current?.ensureRowVisible) {
      tableRef.current.ensureRowVisible(nextRow);
    }
    
    // Focus cell if not editing (wait a bit for row to render if needed)
    setTimeout(() => {
      const cell = tableRef.current?.querySelector(`[data-cell="${nextRow},${nextCol}"]`);
      if (cell) {
        cell.focus();
        // Scroll cell into view if needed
        cell.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    }, 50);
  };
  // Editing logic
  const handleCellDoubleClick = (rowIdx, colIdx) => {
    setEditingCell({ row: rowIdx, col: colIdx });
    setEditValue(preview.rows[rowIdx]?.[colIdx] ?? '');
  };
  const handleInputChange = (e) => setEditValue(e.target.value);
  const handleInputBlur = (rowIdx, colIdx) => {
    if (onCellChange && editValue !== preview.rows[rowIdx][colIdx]) {
      onCellChange(rowIdx, colIdx, editValue);
    }
    setEditingCell(null);
  };
  const handleInputKeyDown = (e, rowIdx, colIdx) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      handleInputBlur(rowIdx, colIdx);
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    }
  };
  // Focus management
  const handleCellFocus = (rowIdx, colIdx) => {
    if (ignoreNextFocus.current) {
      ignoreNextFocus.current = false;
      return;
    }
    setSelectedRange({ start: { row: rowIdx, col: colIdx }, end: { row: rowIdx, col: colIdx } });
  };

  // Context menu handlers
  const handleContextMenu = (e) => {
    e.preventDefault();
    if (!selectedRange) return;
    ignoreNextFocus.current = true;
    
    // Menu dimensions (approximate)
    const menuWidth = 200;
    const menuHeight = 360;
    
    // Check if menu would overflow right edge
    let x = e.clientX;
    let y = e.clientY;
    
    if (x + menuWidth > window.innerWidth) {
      x = window.innerWidth - menuWidth - 10; // 10px margin from edge
    }
    
    // Check if menu would overflow bottom edge
    if (y + menuHeight > window.innerHeight) {
      y = window.innerHeight - menuHeight - 10; // 10px margin from edge
    }
    
    setContextMenu({ x, y });
  };
  const handleMenuAction = (action) => {
    setContextMenu(null);
    if (action === 'translate') handleTranslateSelected(selectedRange);
    if (action === 'undo' && onUndo) onUndo();
    if (action === 'redo' && onRedo) onRedo();
    if (action === 'copy' && onCopy) onCopy(selectedRange);
    if (action === 'paste' && onPaste) onPaste(selectedRange);
    if (action === 'export' && onExportSelected) onExportSelected(selectedRange);
    if (action === 'convert') handleConvertSelection();
  };

  // Currency conversion for selected cell(s)
  const handleConvertSelection = () => {
    if (!selectedRange) return alert('Select a cell or range of cells.');
    setShowCurrencyModal(true);
  };

  // Handle currency conversion or simple multiplication
  const handleCurrencyConversion = async () => {
    const minRow = Math.min(selectedRange.start.row, selectedRange.end.row);
    const maxRow = Math.max(selectedRange.start.row, selectedRange.end.row);
    const minCol = Math.min(selectedRange.start.col, selectedRange.end.col);
    const maxCol = Math.max(selectedRange.start.col, selectedRange.end.col);
    
    let rate = null;
    let rateSource = '';
    
    if (currencyForm.simpleMultiply) {
      // Simple multiplication mode
      if (!currencyForm.manualRate || currencyForm.manualRate.trim().length === 0) {
        alert('Molimo unesite vrednost množioca.');
        return;
      }
      const multiplier = parseFloat(currencyForm.manualRate);
      if (isNaN(multiplier)) {
        alert('Neispravna vrednost množioca. Mora biti broj.');
        return;
      }
      rate = multiplier;
      rateSource = 'množilac';
    } else if (currencyForm.manualRate && currencyForm.manualRate.trim().length > 0) {
      // Manual currency rate (if provided)
      const manualRate = parseFloat(currencyForm.manualRate);
      if (isNaN(manualRate) || manualRate <= 0) {
        alert('Neispravna vrednost kursa. Mora biti pozitivan broj.');
        return;
      }
      rate = manualRate;
      rateSource = 'ručno uneti kurs';
    } else {
      // Automatic currency rate fetch
      // Validate currency code
      if (!currencyForm.currency || currencyForm.currency.trim().length === 0) {
        alert('Molimo unesite kod valute (npr. EUR, USD, GBP).');
        return;
      }
      
      // Validate and format date
      let formattedDate = currencyForm.date;
      if (!formattedDate) {
        alert('Molimo unesite datum.');
        return;
      }
      
      // Ensure date is in YYYY-MM-DD format
      // If date input returns a different format, convert it
      if (formattedDate.includes('/')) {
        // Convert MM/DD/YYYY or DD/MM/YYYY to YYYY-MM-DD
        const dateParts = formattedDate.split('/');
        if (dateParts.length === 3) {
          // Assume MM/DD/YYYY format (US format)
          formattedDate = `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;
        }
      }
      
      // Validate date format
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(formattedDate)) {
        alert('Neispravan format datuma. Koristite format YYYY-MM-DD (npr. 2025-12-11).');
        return;
      }
      
      try {
        const res = await fetch(`${API_ENDPOINTS.EXCHANGE_RATE}/${currencyForm.currency.toUpperCase()}/${formattedDate}`);
        const data = await res.json();
        if (!res.ok) {
          alert(data.error || `Neuspešno učitavanje kursa za ${currencyForm.currency} na datum ${formattedDate}.`);
          return;
        }
        
        if (!data.exchange_middle) {
          alert(`Kurs nije pronađen za ${currencyForm.currency} na datum ${formattedDate}.`);
          return;
        }
        
        rate = parseFloat(data.exchange_middle);
        if (isNaN(rate) || rate <= 0) {
          alert(`Neispravna vrednost kursa: ${data.exchange_middle}`);
          return;
        }
        rateSource = 'automatski učitani kurs';
      } catch (err) {
        console.error('Currency conversion error:', err);
        alert(`Greška pri učitavanju kursa: ${err.message || 'Nepoznata greška'}`);
        return;
      }
    }
    
    // Prepare undo stack
    setUndoStack(prev => [JSON.parse(JSON.stringify(preview.rows)), ...prev]);
    
    // Convert and update cells
    const newRows = preview.rows.map((row, i) =>
      row.map((cell, j) => {
        if (i >= minRow && i <= maxRow && j >= minCol && j <= maxCol && !isNaN(Number(cell))) {
          return (Number(cell) * rate).toFixed(2);
        }
        return cell;
      })
    );
    
    setPreview(prev => {
      const updatedSheets = { ...prev.sheets };
      if (prev.sheetNames && prev.sheetNames[prev.currentSheet]) {
        updatedSheets[prev.sheetNames[prev.currentSheet]] = newRows;
      }
      return { ...prev, rows: newRows, sheets: updatedSheets };
    });
    setShowCurrencyModal(false);
    
    // Trigger auto-save after currency conversion
    triggerAutoSave();
    
    const action = currencyForm.simpleMultiply ? 'Množenje' : 'Konverzija';
    alert(`${action} završen${currencyForm.simpleMultiply ? 'o' : 'a'}!\nVrednost: ${rate} (${rateSource})\nMožete poništiti operaciju pomoću kontekstnog menija.\n\nPromene će biti automatski sačuvane.`);
  };

  // Undo last conversion
  const handleUndoBulk = () => {
    if (undoStack.length === 0) return alert('Nothing to undo.');
    const prevRows = undoStack[0];
    setPreview(prev => ({ ...prev, rows: prevRows }));
    setUndoStack(stack => stack.slice(1));
  };
  const handleCloseMenu = () => setContextMenu(null);

  // Handle cell change - calls parent handler which updates both rows and sheets
  const handleCellChange = (rowIdx, colIdx, newValue) => {
    if (onCellChange) {
      onCellChange(rowIdx, colIdx, newValue);
    }
  };

  // Handle translate selected - calls parent handler which updates sheets
  const handleTranslateSelected = async (selectedRange) => {
    if (onTranslateSelected) {
      await onTranslateSelected(selectedRange);
    }
  };

  // Determine if only a single cell is selected and being edited
  const isSingleCellSelected = selectedRange && selectedRange.start.row === selectedRange.end.row && selectedRange.start.col === selectedRange.end.col;

  return (
    <div style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: colors.card, borderRadius: 8, boxShadow: `0 2px 8px ${colors.shadow}`, padding: 24, position: 'relative', transition: 'background-color 0.3s ease, box-shadow 0.3s ease' }}>

      {/* Draft Prompt Modal */}
      {draftData?.exists && !draftData.loaded && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
        }}>
          <div style={{
            background: colors.card,
            borderRadius: 12,
            padding: 24,
            maxWidth: 500,
            boxShadow: `0 4px 20px ${colors.shadowHover}`,
            transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
          }}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: 20, fontWeight: 700, color: colors.text, transition: 'color 0.3s ease' }}>
              {t('filePreview.draftFound')}
            </h2>
            <p style={{ margin: '0 0 16px 0', color: colors.textSecondary, fontSize: 14, transition: 'color 0.3s ease' }}>
              {t('filePreview.draftMessage')}{draftData.lastSavedBy && ` ${t('filePreview.lastSavedBy', { name: draftData.lastSavedBy.name, date: new Date(draftData.lastSavedAt).toLocaleString() })}`}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={async () => {
                  // Discard draft
                  try {
                    await authenticatedFetchJson(
                      `${API_ENDPOINTS.DELETE_FILE_DRAFT}/${preview.fileId}/draft?sheet=${preview.currentSheet || 0}`,
                      { method: 'DELETE' }
                    );
                    setDraftData({ exists: false, loaded: true });
                    // Reload original file by re-fetching
                    window.location.reload();
                  } catch (err) {
                    console.error('Failed to discard draft:', err);
                    setDraftData({ exists: false, loaded: true });
                  }
                }}
                style={{
                  padding: '8px 16px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: 6,
                  background: colors.background,
                  color: colors.text,
                  cursor: 'pointer',
                  fontSize: 14,
                  transition: 'border-color 0.2s, background-color 0.2s, color 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = colors.backgroundHover}
                onMouseLeave={(e) => e.target.style.backgroundColor = colors.background}
              >
                {t('filePreview.startFresh')}
              </button>
              <button
                onClick={() => {
                  // Load draft
                  setPreview(prev => ({
                    ...prev,
                    rows: draftData.sheetData,
                  }));
                  setDraftData(prev => ({ ...prev, loaded: true }));
                }}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: 6,
                  background: colors.primary,
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: 14,
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.background = colors.primaryHover}
                onMouseLeave={(e) => e.target.style.background = colors.primary}
              >
                {t('filePreview.loadDraft')}
              </button>
              <button
                onClick={() => {
                  setDraftData({ exists: false, loaded: true });
                }}
                style={{
                  padding: '8px 16px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: 6,
                  background: colors.background,
                  color: colors.text,
                  cursor: 'pointer',
                  fontSize: 14,
                  transition: 'border-color 0.2s, background-color 0.2s, color 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = colors.backgroundHover}
                onMouseLeave={(e) => e.target.style.backgroundColor = colors.background}
              >
                {t('filePreview.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 40 }}>
          <div style={{ fontWeight: 700, fontSize: 18 }}>{preview.filename}</div>
          <HelpIcon
            content={getHelpContent('filePreview.cellEditing')?.short}
            detailedContent={getHelpContent('filePreview.cellEditing')?.detailed}
            position="bottom"
            size={16}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
            <button
              onClick={saveDraft}
              disabled={saving || !preview?.fileId}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: 6,
                background: saving ? colors.textLight : colors.primary,
                color: 'white',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => {
                if (!saving) e.target.style.background = colors.primaryHover;
              }}
              onMouseLeave={(e) => {
                if (!saving) e.target.style.background = colors.primary;
              }}
            >
              {saving ? (
                <>
                  <span>💾</span>
                  <span>{t('filePreview.saving')}</span>
                </>
              ) : (
                <>
                  <span>💾</span>
                  <span>{t('filePreview.save')}</span>
                </>
              )}
            </button>
            <HelpIcon
              content={getHelpContent('filePreview.saveDraft')?.short}
              detailedContent={getHelpContent('filePreview.saveDraft')?.detailed}
              position="left"
              size={14}
            />
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 12 }}>
        <SheetSelector sheetNames={preview.sheetNames} currentSheet={preview.currentSheet} onSheetChange={handleSheetChangeWithAutoSave} />
        <HelpIcon
          content={getHelpContent('filePreview.sheetSelector')?.short}
          detailedContent={getHelpContent('filePreview.sheetSelector')?.detailed}
          position="bottom"
          size={14}
        />
      </div>
      
      {/* Auto-save and Last Saved Timestamp Indicators */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {autoSaving && (
          <div style={{
            background: '#f59e0b',
            color: 'white',
            padding: '4px 12px',
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 500,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}>
            <span style={{ 
              display: 'inline-block',
              width: 12,
              height: 12,
              border: '2px solid white',
              borderTop: '2px solid transparent',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }}></span>
            <span>Čuvanje...</span>
          </div>
        )}
        {lastSavedAt && !autoSaving && (
          <div style={{
            background: '#10b981',
            color: 'white',
            padding: '4px 12px',
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 500,
            display: 'inline-block',
          }}>
            💾 Sačuvano: {new Date(lastSavedAt).toLocaleTimeString()}
          </div>
        )}
      </div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      
      {/* Conditional rendering: ScrollableTable for >= 100 rows, regular table for < 100 */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {preview.rows.length >= 100 ? (
        <PaginatedTable
          rows={preview.rows}
          maxCols={maxCols}
          onCellChange={handleCellChange}
          editingCell={editingCell}
          setEditingCell={setEditingCell}
          selectedRange={selectedRange}
          setSelectedRange={setSelectedRange}
          isSelecting={isSelecting}
          setIsSelecting={setIsSelecting}
          editValue={editValue}
          setEditValue={setEditValue}
          tableRef={tableRef}
          onCellMouseDown={handleCellMouseDown}
          onCellMouseOver={handleCellMouseOver}
          onCellDoubleClick={handleCellDoubleClick}
          onCellFocus={handleCellFocus}
          isCellInRange={isCellInRange}
          handleTableKeyDown={handleTableKeyDown}
          handleMouseUp={handleMouseUp}
          handleContextMenu={handleContextMenu}
          isSingleCellSelected={isSingleCellSelected}
          handleInputChange={handleInputChange}
          handleInputBlur={handleInputBlur}
          handleInputKeyDown={handleInputKeyDown}
          hasConflict={hasConflict}
          conflictedCells={conflictedCells}
          conflictMetadata={conflictMetadata}
        />
      ) : (
        <div
          tabIndex={0}
          ref={tableRef}
          onKeyDown={handleTableKeyDown}
          onMouseUp={handleMouseUp}
          onPaste={async (e) => {
            if (selectedRange && onPaste) {
              e.preventDefault();
              // Call the paste handler - it will read from clipboard itself
              onPaste(selectedRange);
            }
          }}
          style={{ 
            outline: 'none', 
            userSelect: isSingleCellSelected && editingCell ? 'auto' : 'none',
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}
          onContextMenu={handleContextMenu}
        >
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <tbody>
              {preview.rows.map((row, i) => (
                <tr key={i}>
                  {Array.from({ length: maxCols }).map((_, j) => {
                    const isSelected = isCellInRange(i, j, selectedRange);
                    const isEditing = editingCell && editingCell.row === i && editingCell.col === j;
                    const cellKey = `${i}-${j}`;
                    const hasConflictCell = conflictedCells.has(cellKey);
                    const conflictInfo = conflictMetadata.get(cellKey);
                    return (
                      <td
                        key={j}
                        data-cell={`${i},${j}`}
                        tabIndex={0}
                        style={{
                          border: isSelected ? '2px solid #2563eb' : '1px solid #e5e7eb',
                          padding: 6,
                          fontSize: 15,
                          maxWidth: 160,
                          maxHeight: 40,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          verticalAlign: 'middle',
                          cursor: 'pointer',
                          background: hasConflictCell ? '#fee2e2' : (isSelected ? '#e0e7ff' : undefined),
                          title: hasConflictCell && conflictInfo ? `Konflikt: Izmenjeno od strane ${conflictInfo.editedBy?.name || 'drugog korisnika'} ${conflictInfo.editedAt ? `u ${new Date(conflictInfo.editedAt).toLocaleString()}` : ''}` : undefined,
                          outline: 'none',
                          userSelect: isEditing ? 'text' : 'none',
                        }}
                        onMouseDown={e => {
                          if (e.button === 2) { e.preventDefault(); return; }
                          handleCellMouseDown(i, j);
                        }}
                        onMouseOver={() => handleCellMouseOver(i, j)}
                        onDoubleClick={() => handleCellDoubleClick(i, j)}
                        onFocus={() => handleCellFocus(i, j)}
                      >
                        {isEditing ? (
                          <input
                            type="text"
                            value={editValue}
                            autoFocus
                            style={{
                              width: '100%',
                              fontSize: 15,
                              border: 'none',
                              outline: 'none',
                              background: colors.background,
                              color: colors.text,
                              padding: 0,
                              margin: 0,
                              userSelect: 'text',
                              transition: 'background-color 0.2s, color 0.2s',
                            }}
                            onChange={handleInputChange}
                            onBlur={() => handleInputBlur(i, j)}
                            onKeyDown={e => handleInputKeyDown(e, i, j)}
                          />
                        ) : (
                          row[j] !== undefined ? row[j] : ''
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>
      {contextMenu && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              background: 'transparent',
            }}
            onClick={handleCloseMenu}
          />
          <div
            style={{
              position: 'fixed',
              top: contextMenu.y,
              left: contextMenu.x,
              background: colors.card,
              border: `1px solid ${colors.border}`,
              borderRadius: 6,
              boxShadow: `0 2px 8px ${colors.shadowHover}`,
              zIndex: 10000,
              minWidth: 140,
              padding: 4,
              transition: 'background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',
            }}
            onMouseLeave={handleCloseMenu}
          >
            <div style={{padding: '8px 12px', cursor: 'pointer', fontSize: 15, color: colors.text, borderRadius: 4, marginBottom: 2, transition: 'background 0.15s, color 0.15s', userSelect: 'none'}} onMouseEnter={(e) => e.target.style.background = colors.backgroundHover} onMouseLeave={(e) => e.target.style.background = 'transparent'} onClick={() => handleMenuAction('translate')}>{t('contextMenu.translate')}</div>
            <div 
              style={{
                padding: '8px 12px', 
                cursor: (undoStack.length > 0 || canUndo) ? 'pointer' : 'not-allowed', 
                fontSize: 15, 
                color: (undoStack.length > 0 || canUndo) ? colors.text : colors.textLight, 
                borderRadius: 4, 
                marginBottom: 2, 
                transition: 'background 0.15s, color 0.15s', 
                userSelect: 'none',
                opacity: (undoStack.length > 0 || canUndo) ? 1 : 0.5
              }} 
              onMouseEnter={(e) => {
                if (undoStack.length > 0 || canUndo) {
                  e.target.style.background = colors.backgroundHover;
                }
              }} 
              onMouseLeave={(e) => e.target.style.background = 'transparent'} 
              onClick={() => {
                if (undoStack.length > 0) {
                  handleUndoBulk();
                } else if (canUndo && onUndo) {
                  onUndo();
                }
              }}
            >
              {t('contextMenu.undo')}
            </div>
            <div style={{padding: '8px 12px', cursor: 'pointer', fontSize: 15, color: colors.text, borderRadius: 4, marginBottom: 2, transition: 'background 0.15s, color 0.15s', userSelect: 'none'}} onMouseEnter={(e) => e.target.style.background = colors.backgroundHover} onMouseLeave={(e) => e.target.style.background = 'transparent'} onClick={() => handleMenuAction('redo')}>{t('contextMenu.redo')}</div>
            <div style={{padding: '8px 12px', cursor: 'pointer', fontSize: 15, color: colors.text, borderRadius: 4, marginBottom: 2, transition: 'background 0.15s, color 0.15s', userSelect: 'none'}} onMouseEnter={(e) => e.target.style.background = colors.backgroundHover} onMouseLeave={(e) => e.target.style.background = 'transparent'} onClick={() => handleMenuAction('copy')}>{t('contextMenu.copy')}</div>
            <div style={{padding: '8px 12px', cursor: 'pointer', fontSize: 15, color: colors.text, borderRadius: 4, marginBottom: 2, transition: 'background 0.15s, color 0.15s', userSelect: 'none'}} onMouseEnter={(e) => e.target.style.background = colors.backgroundHover} onMouseLeave={(e) => e.target.style.background = 'transparent'} onClick={() => handleMenuAction('paste')}>{t('contextMenu.paste')}</div>
            <div style={{padding: '8px 12px', cursor: 'pointer', fontSize: 15, color: colors.text, borderRadius: 4, marginBottom: 2, transition: 'background 0.15s, color 0.15s', userSelect: 'none'}} onMouseEnter={(e) => e.target.style.background = colors.backgroundHover} onMouseLeave={(e) => e.target.style.background = 'transparent'} onClick={() => handleMenuAction('export')}>{t('contextMenu.export')}</div>
            <div style={{padding: '8px 12px', cursor: 'pointer', fontSize: 15, color: colors.text, borderRadius: 4, marginBottom: 2, transition: 'background 0.15s, color 0.15s', userSelect: 'none'}} onMouseEnter={(e) => e.target.style.background = colors.backgroundHover} onMouseLeave={(e) => e.target.style.background = 'transparent'} onClick={() => handleMenuAction('convert')}>{t('contextMenu.convertCurrency')}</div>
          </div>
        </>
      )}

      {/* Currency Conversion Modal */}
      {showCurrencyModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          transition: 'background 0.3s ease'
        }}>
          <div style={{
            background: colors.card,
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: `0 4px 20px ${colors.shadowHover}`,
            transition: 'background-color 0.3s ease, box-shadow 0.3s ease'
          }}>
            <h2 style={{ margin: '0 0 1.5rem 0', color: colors.text, fontSize: '1.5rem', transition: 'color 0.3s ease' }}>
              💱 Konverzija valute / 🔢 Množenje
            </h2>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <input
                  type="checkbox"
                  id="simpleMultiply"
                  checked={currencyForm.simpleMultiply}
                  onChange={(e) => setCurrencyForm(prev => ({ ...prev, simpleMultiply: e.target.checked }))}
                  style={{ width: '18px', height: '18px' }}
                />
                <label htmlFor="simpleMultiply" style={{ fontSize: '14px', fontWeight: 500, color: colors.text, transition: 'color 0.3s ease' }}>
                  Samo pomnoži brojeve (bez konverzije valute)
                </label>
              </div>
            </div>

            {!currencyForm.simpleMultiply && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text, transition: 'color 0.3s ease' }}>
                  Valuta (Currency Code)
                </label>
                <input
                  type="text"
                  value={currencyForm.currency}
                  onChange={(e) => setCurrencyForm(prev => ({ ...prev, currency: e.target.value.toUpperCase() }))}
                  placeholder="EUR, USD, GBP..."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${colors.border}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: colors.background,
                    color: colors.text,
                    transition: 'border-color 0.2s, background-color 0.2s, color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = colors.primary}
                  onBlur={(e) => e.target.style.borderColor = colors.border}
                />
              </div>
            )}

            <div style={{ marginBottom: '1.5rem' }}>
              {currencyForm.simpleMultiply ? (
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text, transition: 'color 0.3s ease' }}>
                    Množilac (npr. 117.5)
                  </label>
                  <input
                    type="number"
                    step="0.000001"
                    value={currencyForm.manualRate}
                    onChange={(e) => setCurrencyForm(prev => ({ ...prev, manualRate: e.target.value }))}
                    placeholder="117.5"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${colors.border}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      backgroundColor: colors.background,
                      color: colors.text,
                      transition: 'border-color 0.2s, background-color 0.2s, color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = colors.primary}
                    onBlur={(e) => e.target.style.borderColor = colors.border}
                  />
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text, transition: 'color 0.3s ease' }}>
                      Kurs (npr. 117.5) - opciono
                    </label>
                    <input
                      type="number"
                      step="0.000001"
                      value={currencyForm.manualRate}
                      onChange={(e) => setCurrencyForm(prev => ({ ...prev, manualRate: e.target.value }))}
                      placeholder="Ostavite prazno za automatsko učitavanje"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${colors.border}`,
                        borderRadius: '8px',
                        fontSize: '14px',
                        backgroundColor: colors.background,
                        color: colors.text,
                        transition: 'border-color 0.2s, background-color 0.2s, color 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = colors.primary}
                      onBlur={(e) => e.target.style.borderColor = colors.border}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text, transition: 'color 0.3s ease' }}>
                      Datum (YYYY-MM-DD) - koristi se ako kurs nije unet
                    </label>
                    <input
                      type="date"
                      value={currencyForm.date}
                      onChange={(e) => setCurrencyForm(prev => ({ ...prev, date: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${colors.border}`,
                        borderRadius: '8px',
                        fontSize: '14px',
                        backgroundColor: colors.background,
                        color: colors.text,
                        transition: 'border-color 0.2s, background-color 0.2s, color 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = colors.primary}
                      onBlur={(e) => e.target.style.borderColor = colors.border}
                    />
                    {preloadedRate !== null && !loadingRate && (
                      <div style={{ 
                        marginTop: '0.5rem', 
                        padding: '0.75rem', 
                        background: isDark ? colors.backgroundLight : '#f0f9ff', 
                        border: `1px solid ${colors.primary}`, 
                        borderRadius: '8px',
                        fontSize: '14px',
                        transition: 'background-color 0.3s ease, border-color 0.3s ease'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: colors.text, transition: 'color 0.3s ease' }}>
                            Učitan kurs za {currencyForm.currency} na {currencyForm.date}: <strong>{preloadedRate.toFixed(6)}</strong>
                          </span>
                          <button
                            onClick={() => setCurrencyForm(prev => ({ ...prev, manualRate: preloadedRate.toString() }))}
                            style={{
                              padding: '0.25rem 0.75rem',
                              background: colors.primary,
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = colors.primaryHover}
                            onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
                          >
                            Koristi
                          </button>
                        </div>
                      </div>
                    )}
                    {loadingRate && (
                      <p style={{ fontSize: '12px', color: colors.textSecondary, margin: '0.5rem 0 0 0', transition: 'color 0.3s ease' }}>
                        Učitavanje kursa...
                      </p>
                    )}
                    {!loadingRate && preloadedRate === null && currencyForm.currency && currencyForm.date && (
                      <p style={{ fontSize: '12px', color: colors.textSecondary, margin: '0.5rem 0 0 0', transition: 'color 0.3s ease' }}>
                        Kurs će biti automatski učitan za izabrani datum ako kurs nije unet ručno
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowCurrencyModal(false);
                  setCurrencyForm({
                    currency: 'EUR',
                    date: new Date().toISOString().slice(0, 10),
                    manualRate: '',
                    simpleMultiply: false
                  });
                  setPreloadedRate(null);
                  setLoadingRate(false);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: colors.textSecondary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = colors.border}
                onMouseLeave={(e) => e.target.style.backgroundColor = colors.textSecondary}
              >
                Otkaži
              </button>
              <button
                onClick={handleCurrencyConversion}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: colors.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = colors.primaryHover}
                onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
              >
                {currencyForm.simpleMultiply ? '🔢 Pomnoži' : '💱 Konvertuj'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// menuItemStyle is now defined inline with theme colors 